import type { CSSProperties, ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { nameDataset } from '@/entities/dataset';
import type { CatalogueNames } from '@/entities/dataset';
import { useDatasetNames } from '@/features/catalog-browser';
import type {
  Incident,
  IncidentKind,
  Severity,
} from '@/shared/api/generated/observability';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useAcknowledgeIncident,
  useIncidents,
  useResolveIncident,
} from '../api/incidents';
import {
  ageReading,
  attributionOf,
  canAcknowledge,
  canResolve,
  groupForQueue,
  INCIDENT_FILTERS,
  shortIncidentId,
  stateLabelKey,
  stateMeaningKey,
  subjectKindOf,
} from '../model/incidents';
import type { IncidentFilter } from '../model/incidents';

/* Severity to a colour token, in one place, and never the only signal.
 *
 * Every card carries its severity as a *word* as well as a hue — WCAG 1.4.1, and this codebase has
 * shipped the failure once already on the classification table, where approved, proposed and stale
 * all rendered "1 column" distinguished by colour alone. */
const SEVERITY_COLOUR: Readonly<Record<Severity, string>> = {
  critical: 'var(--status-error)',
  warning: 'var(--status-warn)',
  info: 'var(--status-info)',
};

/* Every kind this platform can raise, for the second filter.
 *
 * A literal list rather than a set derived from the rows on screen, and that is the point: a filter
 * built from what is present can only ever offer kinds that are already there, so "no freshness
 * breaches" would be indistinguishable from "freshness is not something we monitor". */
const INCIDENT_KINDS: readonly IncidentKind[] = [
  'schema_drift',
  'freshness_breach',
  'volume_anomaly',
  'assessment_overdue',
  'contract_breach',
  'rule_persistent_failure',
];

/** How long the condition has held, in whole units, for a reader rather than for arithmetic. */
function Age({
  incident,
  now,
}: {
  readonly incident: Incident;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const reading = ageReading(incident, now);

  /* `null` means the onset is in the future — clock skew — and "less than a minute" would be a claim
   * this has no basis for. */
  if (reading === null) return <span className="lq-muted">—</span>;
  return (
    <span>{t('incidents.held', { age: t(reading.key, { count: reading.count }) })}</span>
  );
}

/* Who moved it, under the state that says what they did.
 *
 * Rendered even when the name is missing, and that is the point: a state change with nobody against
 * it is a fact an incident review needs to see, not a field to hide. */
function Attribution({ incident }: { readonly incident: Incident }): ReactElement {
  const { t } = useTranslation();
  const attribution = attributionOf(incident);

  /* An open incident has no actor by construction — a monitor opened it. Said in words rather than
   * left blank, because a blank reads as a missing field where this is a fact about how an incident
   * starts, and naming "the system" would put a fiction where a review expects a person. */
  if (attribution === null) {
    return (
      <span title={t('incidents.by.monitorMeaning')}>{t('incidents.by.monitor')}</span>
    );
  }
  return attribution.who === null ? (
    <span title={t('incidents.by.unattributedMeaning')}>
      {t('incidents.by.unattributed')}
    </span>
  ) : (
    <span>{t(`incidents.by.${attribution.verb}`, { who: attribution.who })}</span>
  );
}

function Subject({
  incident,
  names,
}: {
  readonly incident: Incident;
  readonly names: CatalogueNames | null;
}): ReactElement {
  const { t } = useTranslation();

  /* Not a dataset — a contract for `contract_breach`, a rule binding for
   * `rule_persistent_failure`. A dataset lookup would report "this dataset is not in the
   * catalogue" about something that was never one, which is a false statement rather than a
   * missing one, and naming the wrong *kind* of subject is the same error one step earlier. No
   * link either: this screen has a route to neither. */
  const subject = subjectKindOf(incident.kind);
  if (subject !== 'dataset') {
    return (
      <span>
        {t(`incidents.subject.${subject}`)}{' '}
        <span className="lq-muted">{t('incidents.subjectNotADataset')}</span>
      </span>
    );
  }

  const naming = nameDataset(incident.subject_id, names);
  return (
    <span>
      {t('incidents.subject.dataset')}{' '}
      {/* **The link stays live even when the name does not resolve.** A dataset harvested since this
          page loaded still deserves a working link, so only the *text* changes — the reader is told
          which kind of gap this is and the link still goes to the right place. */}
      <Link className="lq-mono" to={`/datasets/${incident.subject_id}`}>
        {naming.kind === 'named' ? (
          naming.name
        ) : (
          <span className="lq-muted">{t(naming.reasonKey)}</span>
        )}
      </Link>
    </span>
  );
}

/** One incident, as a card.
 *
 * A card and not a table row, and this is the one screen in the product where that is right: every
 * other listing is a set of comparable things scanned down a column, where an incident is a thing
 * that *happened* and what a reader needs is the sentence. In a table that sentence is a cell
 * competing with five others for width, and the first thing it loses is its ending.
 */
function IncidentCard({
  incident,
  names,
  now,
  onActed,
}: {
  readonly incident: Incident;
  readonly names: CatalogueNames | null;
  readonly now: Date;
  readonly onActed: (incident: Incident, action: 'acknowledged' | 'resolved') => void;
}): ReactElement {
  const { t } = useTranslation();
  const acknowledge = useAcknowledgeIncident();
  const resolve = useResolveIncident();
  const failure = acknowledge.error ?? resolve.error;

  return (
    <article
      className="lq-inc"
      style={{ '--inc-colour': SEVERITY_COLOUR[incident.severity] } as CSSProperties}
    >
      <div className="lq-inc__head">
        <span className="lq-inc__severity">
          {t(`incidents.severity.${incident.severity}`)}
        </span>
        <strong className="lq-inc__kind">{t(`incidents.kind.${incident.kind}`)}</strong>
        <span className="lq-inc__id lq-mono">{shortIncidentId(incident.id)}</span>
        <span className="lq-inc__spacer" />
        {/* The state's meaning, not just its name. "Acknowledged" alone reads as done; "seen, and
            the condition still holds" is what a reader needs before deciding whether to act. */}
        <span className="lq-inc__state" title={t(stateMeaningKey(incident.state))}>
          {t(stateLabelKey(incident.state))}
        </span>
      </div>

      <p className="lq-inc__summary">{incident.summary}</p>

      <div className="lq-inc__meta">
        <Subject incident={incident} names={names} />
        <Age incident={incident} now={now} />
        <Attribution incident={incident} />
      </div>

      <div className="lq-inc__foot">
        <Link className="lq-inc__open" to={`${SCREEN_PATHS.incidents}/${incident.id}`}>
          {t('incidents.open')}
        </Link>
        <span className="lq-inc__spacer" />
        {canAcknowledge(incident) ? (
          <button
            type="button"
            className="lq-inc__ghost"
            disabled={acknowledge.isPending}
            onClick={() => {
              acknowledge.mutate(incident.id, {
                onSuccess: () => {
                  onActed(incident, 'acknowledged');
                },
              });
            }}
          >
            {t('incidents.action.acknowledge')}
          </button>
        ) : (
          /* Muted text rather than a disabled button once it is acknowledged, because the server
           * refuses the repeat with a 409 — not offering an action is a better failure than offering
           * it and rejecting it. Still *said*, so the absence of the button is legible rather than
           * looking like a control that failed to render. */
          incident.state === 'acknowledged' && (
            <span
              className="lq-inc__done"
              title={t('incidents.blocked.alreadyAcknowledged')}
            >
              {t('incidents.state.acknowledged.label')}
            </span>
          )
        )}
        {canResolve(incident) ? (
          <button
            type="button"
            className="lq-inc__primary"
            disabled={resolve.isPending}
            onClick={() => {
              resolve.mutate(incident.id, {
                onSuccess: () => {
                  onActed(incident, 'resolved');
                },
              });
            }}
          >
            {t('incidents.action.resolve')}
          </button>
        ) : (
          <span className="lq-inc__resolved">{t('incidents.resolvedNote')}</span>
        )}
      </div>

      {failure !== null && (
        /* The server's own words, on the card the reader just pressed. On this screen a 409 almost
            always means a colleague acted first, and "it is no longer open" tells them to re-read
            rather than retry. */
        <div className="lq-inc__error">
          <Alert tone="error">{failure.message}</Alert>
        </div>
      )}
    </article>
  );
}

export function IncidentQueue(): ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<IncidentFilter>('active');
  const [kind, setKind] = useState<string | null>(null);
  const [acted, setActed] = useState<{
    readonly incident: Incident;
    readonly action: 'acknowledged' | 'resolved';
  } | null>(null);

  const incidents = useIncidents(filter, kind);
  /* Resolved from the catalog list rather than per row: one query for the page, sharing the cache
   * the catalog browser already fills. A `useDataset` per row would be the N+1 the list endpoint
   * exists to avoid, and a failure here degrades to a stated gap rather than blanking the queue. */
  const names = useDatasetNames();
  /* The active count is the active queue's, whatever filter is showing — a reader looking through
   * resolved incidents still needs to know how many are open. Its own read, so switching filters
   * does not change the number in the header, and free when the filter is already `active`. */
  const active = useIncidents('active', null);
  const now = new Date();

  const filters = (
    <div className="lq-incfilters">
      <span className="lq-incfilters__label">{t('incidents.filter.state')}</span>
      {INCIDENT_FILTERS.map((each) => (
        <button
          key={each}
          type="button"
          className={each === filter ? 'lq-incchip lq-incchip--on' : 'lq-incchip'}
          aria-pressed={each === filter}
          onClick={() => {
            setFilter(each);
            setActed(null);
          }}
        >
          {t(`incidents.filter.${each}`)}
        </button>
      ))}
      <span className="lq-incfilters__label lq-incfilters__label--kind">
        {t('incidents.filter.kind')}
      </span>
      <select
        className="lq-incfilters__select"
        aria-label={t('incidents.filter.kind')}
        value={kind ?? 'all'}
        onChange={(event) => {
          setKind(event.target.value === 'all' ? null : event.target.value);
          setActed(null);
        }}
      >
        <option value="all">{t('incidents.filter.everyKind')}</option>
        {INCIDENT_KINDS.map((each) => (
          <option key={each} value={each}>
            {t(`incidents.kind.${each}`)}
          </option>
        ))}
      </select>
    </div>
  );

  const header = (
    <>
      <PageHeader
        title={t('incidents.title')}
        lede={t('incidents.subtitle')}
        meta={
          /* **The active count, and deliberately never a resolved-today count.** The reference
             carries both; this platform can honestly produce only the first. `/incidents/all`
             filters on `opened_at` and there is no `resolved_after`, so "resolved today" would have
             to be counted from whichever page came back — understating it silently the moment that
             page truncated. A number that is sometimes wrong is worse than one that is absent. */
          active.isSuccess ? (
            <span>{t('incidents.activeCount', { count: active.data.items.length })}</span>
          ) : undefined
        }
      />
      {filters}
    </>
  );

  if (incidents.isPending) {
    return (
      <>
        {header}
        <p role="status" className="lq-muted">
          {t('incidents.loading')}
        </p>
        <SkeletonRows rows={4} columns={6} />
      </>
    );
  }

  if (incidents.isError) {
    return (
      <>
        {header}
        <Alert tone="error">
          {t('incidents.unreachable', { detail: incidents.error.message })}
        </Alert>
      </>
    );
  }

  const page = incidents.data;
  const groups = groupForQueue(page.items);
  const unfiltered = filter === 'active' && kind === null;

  return (
    <>
      {header}

      {acted !== null && (
        /* **What the transition recorded, said in place.** A reader is working down a queue and may
           act on three incidents, so navigating away after the first would lose their place. The
           banner names the incident and states the thing the state machine will not: acknowledged
           is not resolved, and resolving suppresses nothing. */
        <div className="lq-incmsg" role="status">
          <strong className="lq-incmsg__title">
            {t(`incidents.acted.${acted.action}.title`, {
              incident: shortIncidentId(acted.incident.id),
            })}
          </strong>
          <span className="lq-incmsg__body">
            {t(`incidents.acted.${acted.action}.body`)}
          </span>
        </div>
      )}

      {groups.length === 0 ? (
        /* Filter-aware, because an empty view is a filter result rather than a statement about the
         * estate — and on the default filter it *is* a statement about the estate, so the two are
         * worded differently. */
        <EmptyState
          title={t(unfiltered ? 'incidents.emptyTitle' : 'incidents.emptyFiltered')}
          guidance={t(unfiltered ? 'incidents.empty' : 'incidents.emptyFilteredNote')}
        />
      ) : (
        <div>
          {groups.map((group) => (
            <section
              key={group.severity}
              className="lq-incgroup"
              style={{ '--inc-colour': SEVERITY_COLOUR[group.severity] } as CSSProperties}
            >
              <div className="lq-incgroup__head">
                <span className="lq-incgroup__dot" aria-hidden="true" />
                <h3 className="lq-incgroup__label">
                  {t(`incidents.severity.${group.severity}`)}
                </h3>
                <span className="lq-incgroup__count">
                  {t('incidents.groupCount', { count: group.incidents.length })}
                </span>
                <span className="lq-inc__spacer" />
                {/* What this severity *means*, on the heading rather than in a tooltip: it is the
                    difference between a fault and a record, which is what tells somebody whether
                    they can stop reading at the end of this group. */}
                <span className="lq-incgroup__meaning">
                  {t(`incidents.severityMeaning.${group.severity}`)}
                </span>
              </div>
              <div className="lq-incgroup__cards">
                {group.incidents.map((incident) => (
                  <IncidentCard
                    key={incident.id}
                    incident={incident}
                    names={names.data ?? null}
                    now={now}
                    onActed={(each, action) => {
                      setActed({ incident: each, action });
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* One sentence the screen owes every reader, and two it owes only sometimes. `atQueueCap` is
          true when the active queue came back full, in which case a kind narrowing narrowed the 200
          most severe rather than the whole set — filter-then-truncate and truncate-then-filter are
          different answers, and a reader cannot tell which they have unless the screen says. */}
      <p className="lq-muted lq-incfoot">{t('incidents.footnote')}</p>
      {page.atQueueCap && (
        <p className="lq-muted lq-incfoot">
          {t('incidents.cappedNote', { cap: page.items.length })}
        </p>
      )}
      {page.nextCursor !== null && (
        <p className="lq-muted lq-incfoot">{t('incidents.morePages')}</p>
      )}
    </>
  );
}
